/**
 * BatchResultsTable — Bloomberg terminal density batch results table.
 *
 * 13 columns: checkbox, rank, address, composite, IRR, EM, cap rate,
 * zandi, NOI, offer price, units, market, verdict.
 *
 * Features:
 *  - Rank badge in accent-colored circle (#1, #2, …)
 *  - Verdict row tinting (GO green, NO-GO red, CONDITIONAL amber)
 *  - Stage 1 null fields show "—" (not "N/A")
 *  - Sticky header, horizontal scroll, dense px-2 py-2 padding
 *  - Sortable columns via onSort callback
 *  - Error rows highlighted with AlertCircle icon
 */

import { AlertCircle, ChevronUp, ChevronDown } from 'lucide-react';
import type { BatchPropertyResult } from '../../types/batch';

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

function formatCurrency(value: number | null | undefined): string {
  if (value == null) return '—';
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(2)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
}

function formatPercent(value: number | null | undefined): string {
  if (value == null) return '—';
  // Backend returns percentage values (e.g., 9.49 for 9.49%, not 0.0949)
  // Heuristic: absolute value > 1 means already a percentage
  if (Math.abs(value) > 1) return `${value.toFixed(1)}%`;
  return `${(value * 100).toFixed(1)}%`;
}

function formatComposite(value: number | null | undefined): string {
  if (value == null) return '—';
  return value.toFixed(1);
}

function formatEM(value: number | null | undefined): string {
  if (value == null) return '—';
  return `${value.toFixed(2)}x`;
}

// ---------------------------------------------------------------------------
// Row tinting
// ---------------------------------------------------------------------------

function getRowTint(verdict: string | null | undefined): string {
  switch (verdict) {
    case 'GO': return 'bg-emerald-500/5';
    case 'NO-GO': return 'bg-red-500/5';
    case 'CONDITIONAL': return 'bg-amber-500/5';
    default: return '';
  }
}

// ---------------------------------------------------------------------------
// Verdict pill
// ---------------------------------------------------------------------------

function VerdictPill({ verdict }: { verdict: string | null | undefined }) {
  if (verdict == null) {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-gray-500/15 text-text-secondary border border-border">
        PENDING
      </span>
    );
  }
  const colors: Record<string, string> = {
    'GO': 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
    'NO-GO': 'bg-red-500/15 text-red-400 border-red-500/30',
    'CONDITIONAL': 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  };
  const cls = colors[verdict] ?? 'bg-gray-500/15 text-text-secondary border-border';
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold border ${cls}`}>
      {verdict}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Sort header helper
// ---------------------------------------------------------------------------

function SortHeader({
  label,
  colKey,
  sortBy,
  sortDescending,
  onSort,
  className = '',
}: {
  label: string;
  colKey: string;
  sortBy: string;
  sortDescending: boolean;
  onSort: (col: string) => void;
  className?: string;
}) {
  const active = sortBy === colKey;
  return (
    <th
      className={`px-2 py-2 text-[11px] font-semibold text-text-secondary uppercase tracking-wide border-b border-border cursor-pointer select-none hover:text-text-primary whitespace-nowrap ${className}`}
      onClick={() => onSort(colKey)}
    >
      <div className="flex items-center gap-0.5">
        <span>{label}</span>
        {active && (
          sortDescending
            ? <ChevronDown className="w-3 h-3 text-accent" />
            : <ChevronUp className="w-3 h-3 text-accent" />
        )}
      </div>
    </th>
  );
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface BatchResultsTableProps {
  results: BatchPropertyResult[];
  sortBy: string;
  sortDescending: boolean;
  onSort: (column: string) => void;
  onPropertyClick?: (propertyId: number) => void;
  selectedIds?: Set<number>;
  onToggleSelect?: (id: number) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function BatchResultsTable({
  results,
  sortBy,
  sortDescending,
  onSort,
  onPropertyClick,
  selectedIds,
  onToggleSelect,
}: BatchResultsTableProps) {
  if (results.length === 0) {
    return (
      <div className="py-10 text-center text-text-secondary text-sm">
        No batch results to display
      </div>
    );
  }

  const showCheckbox = !!onToggleSelect;

  return (
    <div className="overflow-x-auto max-h-[70vh] overflow-y-auto rounded-lg border border-border">
      <table className="w-full text-xs">

        {/* Sticky header */}
        <thead className="sticky top-0 z-10 bg-bg-secondary">
          <tr>
            {showCheckbox && (
              <th className="px-2 py-2 w-8 border-b border-border" />
            )}
            {/* Rank — not sortable (position in sorted list) */}
            <th className="px-2 py-2 text-[11px] font-semibold text-text-secondary uppercase tracking-wide border-b border-border whitespace-nowrap w-10">
              #
            </th>
            <SortHeader label="Address" colKey="address" sortBy={sortBy} sortDescending={sortDescending} onSort={onSort} className="text-left min-w-[200px]" />
            <SortHeader label="Score" colKey="composite_score" sortBy={sortBy} sortDescending={sortDescending} onSort={onSort} className="text-right w-[64px]" />
            <SortHeader label="IRR 5yr" colKey="irr_5yr" sortBy={sortBy} sortDescending={sortDescending} onSort={onSort} className="text-right w-[72px]" />
            <SortHeader label="EM" colKey="equity_multiple" sortBy={sortBy} sortDescending={sortDescending} onSort={onSort} className="text-right w-[60px]" />
            <SortHeader label="Cap Rate" colKey="cap_rate" sortBy={sortBy} sortDescending={sortDescending} onSort={onSort} className="text-right w-[72px]" />
            <SortHeader label="Zandi" colKey="zandi_composite" sortBy={sortBy} sortDescending={sortDescending} onSort={onSort} className="text-right w-[60px]" />
            <SortHeader label="NOI" colKey="noi" sortBy={sortBy} sortDescending={sortDescending} onSort={onSort} className="text-right w-[80px]" />
            <SortHeader label="Offer" colKey="offer_range_high" sortBy={sortBy} sortDescending={sortDescending} onSort={onSort} className="text-right w-[80px]" />
            <SortHeader label="Units" colKey="total_units" sortBy={sortBy} sortDescending={sortDescending} onSort={onSort} className="text-center w-[50px]" />
            <SortHeader label="Market" colKey="neighborhood" sortBy={sortBy} sortDescending={sortDescending} onSort={onSort} className="text-left w-[100px]" />
            <SortHeader label="Verdict" colKey="deal_verdict" sortBy={sortBy} sortDescending={sortDescending} onSort={onSort} className="text-center w-[90px]" />
          </tr>
        </thead>

        {/* Body */}
        <tbody>
          {results.map((result, index) => {
            const isError = result.status === 'failed';
            const isSelected = selectedIds?.has(result.property_id) ?? false;
            const rowTint = getRowTint(result.deal_verdict);
            const rank = index + 1;

            return (
              <tr
                key={result.property_id}
                className={`
                  transition-colors
                  ${isError ? 'bg-red-900/10 hover:bg-red-900/20' : rowTint || (index % 2 === 0 ? 'bg-bg-card' : 'bg-bg-secondary')}
                  ${isSelected ? 'ring-1 ring-inset ring-accent/40' : ''}
                  ${onPropertyClick && !isError ? 'cursor-pointer hover:opacity-80' : ''}
                `}
                onClick={() => !isError && onPropertyClick?.(result.property_id)}
              >
                {/* Checkbox */}
                {showCheckbox && (
                  <td className="px-2 py-2 border-b border-border/50 text-center" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => onToggleSelect?.(result.property_id)}
                      className="w-3.5 h-3.5 accent-accent cursor-pointer"
                    />
                  </td>
                )}

                {/* Rank badge */}
                <td className="px-2 py-2 border-b border-border/50 text-center">
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-accent/10 text-accent text-[10px] font-bold">
                    {rank}
                  </span>
                </td>

                {/* Address */}
                <td className="px-2 py-2 border-b border-border/50 text-left min-w-[200px]">
                  {isError ? (
                    <div className="flex items-center gap-1.5">
                      <AlertCircle className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />
                      <span className="truncate text-text-secondary">{result.address ?? 'Unknown address'}</span>
                    </div>
                  ) : (
                    <span className="truncate block max-w-[240px]">{result.address ?? '—'}</span>
                  )}
                </td>

                {/* Composite score — bold */}
                <td className="px-2 py-2 border-b border-border/50 text-right font-bold text-text-primary">
                  {formatComposite(result.composite_score)}
                </td>

                {/* IRR 5yr (Stage 2 only) */}
                <td className="px-2 py-2 border-b border-border/50 text-right text-text-secondary">
                  {formatPercent(result.irr_5yr)}
                </td>

                {/* Equity Multiple (Stage 2 only) */}
                <td className="px-2 py-2 border-b border-border/50 text-right text-text-secondary">
                  {formatEM(result.equity_multiple)}
                </td>

                {/* Cap Rate */}
                <td className="px-2 py-2 border-b border-border/50 text-right">
                  {formatPercent(result.cap_rate)}
                </td>

                {/* Zandi composite */}
                <td className="px-2 py-2 border-b border-border/50 text-right text-text-secondary">
                  {formatComposite(result.zandi_composite)}
                </td>

                {/* NOI */}
                <td className="px-2 py-2 border-b border-border/50 text-right">
                  {formatCurrency(result.noi)}
                </td>

                {/* Offer price (offer_range_high, Stage 2 only) */}
                <td className="px-2 py-2 border-b border-border/50 text-right text-text-secondary">
                  {formatCurrency(result.offer_range_high)}
                </td>

                {/* Units */}
                <td className="px-2 py-2 border-b border-border/50 text-center">
                  {result.total_units ?? '—'}
                </td>

                {/* Market (neighborhood) */}
                <td className="px-2 py-2 border-b border-border/50 text-left text-text-secondary truncate max-w-[100px]">
                  {result.neighborhood ?? '—'}
                </td>

                {/* Verdict */}
                <td className="px-2 py-2 border-b border-border/50 text-center">
                  <VerdictPill verdict={result.deal_verdict} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default BatchResultsTable;
